import * as Sentry from "@sentry/react";

import { OrgRecord } from "@/shared/domain/orgs";
import { UserRecord } from "@/shared/domain/users";
import { Env, isProductionLikeStage } from "@/shared/environment";

import { RobotoDomainException } from "../http/exceptions";

import { LoggerService } from "./LoggerService";

class ErrorMonitoringService {
  constructor() {
    if (isProductionLikeStage()) {
      Sentry.init({
        dsn: Env.sentryDsn,
        environment: Env.appEnv,

        /**
         * These are unactionable network errors where the browser cancels the
         * request and throws a generic error message.
         *
         * This can happen when navigating pages quickly or refreshing the page.
         */
        ignoreErrors: [
          "TypeError: Failed to fetch", // Chrome
          "TypeError: NetworkError when attempting to fetch resource.", // Firefox
          "TypeError: Load failed", // Safari
        ],
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
          // https://docs.sentry.io/platforms/javascript/configuration/integrations/extraerrordata/
          Sentry.extraErrorDataIntegration({
            captureErrorCause: true,
          }),
        ],

        /* Sets the maximum number of levels to which Sentry will serialize extra data */
        normalizeDepth: 10,

        /*************
         *  Tracing  *
         *************/
        // Capture only 10% of the transactions. Sentry's recommendation is to start small
        // then ramp up once we have a stable baseline.
        tracesSampleRate: 0.1,
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["localhost", Env.robotoServiceBaseUrl],

        /********************
         *  Session Replay  *
         ********************/
        // We will disable general session replays and only record sessions when errors occur.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
      });
    }
  }

  public captureError(error: unknown, extraData?: Record<string, unknown>) {
    LoggerService.error(error, extraData);

    // We don't want expected and gracefully handled 4XX errors to be reported as Sentry issues, so we can inspect the
    // error and manually filter them out
    if (error instanceof RobotoDomainException && error.isClientError()) {
      return;
    }

    Sentry.captureException(error, {
      extra: extraData,
    });
  }

  public setUser(user: UserRecord) {
    Sentry.setUser({
      id: user.user_id,
      email: user.user_id,
      name: user.name,
    });
  }

  public setOrg(org: OrgRecord) {
    Sentry.setContext("Organization", {
      ...org,
    });
  }
}

const ErrorMonitoringServiceInstance = new ErrorMonitoringService();
export { ErrorMonitoringServiceInstance as ErrorMonitoringService };
